import { useMemo } from "react";
import { W, H, project, MAJOR_CITIES } from "./constants";

// Collision-aware label culling for zoom ≥ 2×: country labels win over city labels.
export function useLabelCulling(worldPaths, view) {
  const zoomK = W / view.w;
  const culled = useMemo(() => {
    if (zoomK < 2) return { culledCountryLabels: [], culledCityLabels: [] };
    const boxes = [];
    const inView = (x, y) => x >= view.x && x <= view.x + view.w && y >= view.y && y <= view.y + view.h;
    const overlaps = (b) => boxes.some((o) => !(b[2] < o[0] || b[0] > o[2] || b[3] < o[1] || b[1] > o[3]));
    const countryFs = Math.max(6, 9 * (view.w / W));
    const cityFs = Math.max(5, 7 * (view.w / W));
    const cw = 0.55;
    const pad = 3.5;

    const acceptedCountries = [];
    const countries = worldPaths
      .filter((p) => p.area >= 0.003 && inView(p.cx, p.cy))
      .sort((a, b) => (b.area || 0) - (a.area || 0));
    for (const p of countries) {
      const halfW = (p.name.length * countryFs * cw) / 2 + pad;
      const halfH = countryFs / 2 + pad;
      const box = [p.cx - halfW, p.cy - halfH, p.cx + halfW, p.cy + halfH];
      if (overlaps(box)) continue;
      boxes.push(box);
      acceptedCountries.push(p);
    }

    const acceptedCities = [];
    const cities = MAJOR_CITIES
      .map(([name, la, ln]) => { const [x, y] = project(la, ln, W, H); return { name, x, y }; })
      .filter((c) => inView(c.x, c.y));
    for (const c of cities) {
      const halfW = (c.name.length * cityFs * cw) / 2 + pad;
      const halfH = cityFs / 2 + pad;
      const bx = c.x + 3 + (c.name.length * cityFs * cw) / 2;
      const by = c.y - 3 - cityFs / 2;
      const box = [bx - halfW, by - halfH, bx + halfW, by + halfH];
      if (overlaps(box)) continue;
      boxes.push(box);
      acceptedCities.push(c);
    }
    return { culledCountryLabels: acceptedCountries, culledCityLabels: acceptedCities };
  }, [worldPaths, view.x, view.y, view.w, view.h, zoomK]);
  return { zoomK, ...culled };
}
